<script lang="ts">
    // import Dropzone from '../Dropzone.svelte'
    import Dropzone from 'svelte-file-dropzone'
    import { readable, derived as sderived, writable, type Readable, type Writable } from 'svelte/store'

    import { selectSrc } from '../component_helpers'
    import Icon from '@iconify/svelte'
    import type { Editor } from '@tiptap/core'
    import { orderBy } from 'lodash-es'
    import { DateTime } from 'luxon'

    import { selectSimpleImage } from 'hydria/attachment_helpers'
    import type { HydriaDocument, IImage } from 'hydria/HydriaDocument'
    import { gettext as _ } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { session } from 'hydria/services/session'
    import { makeSortString } from 'hydria/utils/strings'

    import Modal from '../Modal.svelte'

    interface Props {
        editor: Editor
        Document?: HydriaDocument | undefined
        previewName?: string

        close: () => void
    }

    let { editor, Document = undefined, previewName = 'admin', close }: Props = $props()

    let images: Readable<IImage[]> = $state()
    let allowedMimetypes: Writable<Set<string>> = $state()

    if (Document) {
        images = sderived(Document.attachedImages, ($atts) => $atts)
        allowedMimetypes = Document.allowedImageMimetypes
    } else {
        images = readable([])
        allowedMimetypes = writable(new Set())
    }

    interface LImage extends IImage {
        _type: 'image'
    }

    const current = editor.getAttributes('image')
    let imgId: string | undefined = $state(current.aid ? current.aid : undefined)
    let imgUri = $state(current.src ? current.src : '')
    let imgAlt = $state(current.alt ? current.alt : '')
    let imgTitle = $state(current.title ? current.title : '')
    let imgPosition: 'float-left' | 'float-right' | 'center' = $state(current.position ? current.position : 'center')

    function insertImage() {
        if (imgId) {
            const att = $images.filter((o) => o.id === imgId)[0]
            if (att) {
                const sel = selectSimpleImage(att, 512)
                if (sel) imgUri = sel.url
            }
        }
        editor
            .chain()
            .focus()
            .setImage({
                src: imgUri,
                alt: imgAlt ? imgAlt : null,
                title: imgTitle ? imgTitle : null,
                position: imgPosition ? imgPosition : undefined,
                aid: imgId ? imgId : undefined,
            })
            .run()
        close()
    }

    // #############################################################  Upload stuff
    interface LUpload {
        _type: 'upload'
        file: File
        progress: number
        started: DateTime
        ended?: DateTime
        status: 'uploading' | 'aborted' | 'finished' | 'error'
        xhr: XMLHttpRequest
        // formElement: HTMLFormElement
        // formData: FormData
        previewImage?: string
    }
    let uploads: Writable<LUpload[]> = writable([])
    let imagesAndUploads: Readable<Array<LUpload | LImage>> = sderived([images, uploads], ([$images, $uploads]) => {
        return orderBy(
            [
                ...$images.map((i) => {
                    ;(i as LImage)._type = 'image'
                    return i as LImage
                }),
                ...$uploads,
            ],
            [
                (i: LImage | LUpload) => {
                    return makeSortString(i._type === 'upload' ? i.file.name : i.filename)
                },
            ],
            ['asc'],
        )
    })

    function setupFileUpload(file: File) {
        // const formElement = document.createElement('form')
        const upload: LUpload = {
            _type: 'upload',
            file: file,
            progress: 0,
            status: 'uploading',
            started: DateTime.now(),
            xhr: new XMLHttpRequest(),
            // formElement: formElement,
            // formData: new FormData(formElement),
            previewImage: undefined,
        }
        uploads.update(($uploads) => {
            $uploads.push(upload)
            return $uploads
        })
        const formData = new FormData()
        formData.append('file', file)

        const xhr = upload.xhr

        if (['image/jpeg', 'image/avif', 'image/webp', 'image/svg+xml', 'image/png'].includes(file.type)) {
            const reader = new FileReader()
            reader.onload = (e) => {
                upload.previewImage = (e.target as any).result as string
                // To trigger reactivity :|
                uploads.update(($uploads) => $uploads)
            }
            reader.readAsDataURL(file)
        }

        xhr.onreadystatechange = () => {
            if (xhr.readyState == XMLHttpRequest.DONE) {
                let resp: any = {
                    status: xhr.status,
                    data: null,
                }
                if (xhr.responseText) {
                    try {
                        resp.data = JSON.parse(xhr.responseText)
                    } catch (e) {}
                }

                upload.ended = DateTime.now()

                if (resp.status !== 200) {
                    upload.status = 'error'
                } else {
                    upload.status = 'finished'
                    if (Document) Document.updateAttachmentList(resp.data.attachments, resp.data.newly_uploaded_id)
                }
            }
        }
        function remove() {
            uploads.update(($uploads) => {
                const i = $uploads.indexOf(upload)
                if (i >= 0) $uploads.splice(i, 1)
                return $uploads
            })
        }
        xhr.upload.addEventListener(
            'load',
            (e) => {
                upload.progress = 100
                remove()
            },
            false,
        )
        xhr.upload.addEventListener(
            'progress',
            (e) => {
                upload.progress = Math.round((e.loaded * 100) / e.total)
            },
            false,
        )
        xhr.upload.addEventListener(
            'error',
            (e) => {
                upload.status = 'error'
                upload.ended = DateTime.now()
                remove()
            },
            false,
        )
        xhr.upload.addEventListener(
            'abort',
            (e) => {
                upload.status = 'aborted'
                upload.ended = DateTime.now()
                remove()
            },
            false,
        )
        // @ts-expect-error
        xhr.open('POST', `/json/cms/1/upload/${Document.docType}/${Document.docId}`, true)
        xhr.setRequestHeader('X-Lg', $lg)
        // @ts-expect-error
        xhr.setRequestHeader('X-Hydria-Token', session.token)
        xhr.setRequestHeader('Accept', 'application/json')
        xhr.send(formData)
    }

    function handleFile(e: any) {
        if (!e.detail.acceptedFiles.length) return

        for (const file of e.detail.acceptedFiles as File[]) {
            setupFileUpload(file)
        }
    }
</script>

<style>
    img {
        width: 100%;
        height: 4rem;
        object-fit: contain;
    }

    .img-wrap,
    .img-spinner {
        flex: 1 0 0;
        display: grid;
        justify-content: center;
        align-content: center;
    }
    .img-wrap > * {
        grid-row: 1 / 1;
        grid-column: 1 / 1;
    }
    .img-spinner {
        z-index: 1;
    }

    .img-button {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 0.25rem;
        max-width: 100%;
        overflow: hidden;
    }
    .img-desc {
        font-size: 0.75em;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
</style>

<Modal modalClasses="modal-lg" close={() => close()}>
    <div class="modal-header text-bg-primary">
        <div>{$_('Image')}</div>
    </div>
    <div class="modal-body imged d-grid gap-1">
        <div class="d-flex gap-3">
            <div class="col-2 text-align-end"><label for="img-url">{$_('URI :')}</label></div>
            <div class="col"><input type="text" class="form-control" bind:value={imgUri} disabled={imgId != null} placeholder={$_('Lien de l’image (https://…)')} /></div>
        </div>
        <div class="d-flex gap-3">
            <div class="col-2 text-align-end"><label for="img-alt">{$_('Alt :')}</label></div>
            <div class="col"><input type="text" class="form-control" bind:value={imgAlt} placeholder={$_('Texte de remplacement pour les lecteurs (ex: aveugles/faible vision), et si l’image ne charge pas.')} /></div>
        </div>
        <div class="d-flex gap-3">
            <div class="col-2 text-align-end"><label for="img-alt">{$_('Titre :')}</label></div>
            <div class="col"><input type="text" class="form-control" bind:value={imgTitle} placeholder={$_('Affiché quand on place le curseur sur l’image.')} /></div>
        </div>
        <div class="d-flex gap-3">
            <div class="col-2 text-align-end"><label for="img-alt">{$_('Position :')}</label></div>
            <div class="col">
                <div class="btn-group">
                    <button type="button" class="btn btn-outline-secondary" class:active={imgPosition === 'center'} data-tooltip={$_('Centrer')} onclick={() => (imgPosition = 'center')}><Icon inline icon="gridicons:align-image-center" /></button>
                    <button type="button" class="btn btn-outline-secondary" class:active={imgPosition === 'float-left'} data-tooltip={$_('Flottante à gauche')} onclick={() => (imgPosition = 'float-left')}><Icon inline icon="gridicons:align-image-left" /></button>
                    <button type="button" class="btn btn-outline-secondary" class:active={imgPosition === 'float-right'} data-tooltip={$_('Flottante à droite')} onclick={() => (imgPosition = 'float-right')}><Icon inline icon="gridicons:align-image-right" /></button>
                </div>
            </div>
        </div>
    </div>
    {#if $imagesAndUploads}
        <div class="modal-body text-bg-secondary pt-2 pb-2">
            <div>{$_('Images liées à ce document')}</div>
        </div>
        <div class="modal-body image-sel">
            <div class="container">
                <div class="row row-cols-6">
                    <div class="d-grid p-1">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            class:active={imgId == null}
                            onclick={() => {
                                imgId = undefined
                                imgUri = ''
                                imgAlt = ''
                            }}
                        >
                            <em>{$_('Image externe')}</em>
                        </button>
                    </div>
                    {#each $imagesAndUploads as att}
                        <div class="d-grid p-1">
                            {#if att._type === 'upload'}
                                <button type="button" class="btn btn-outline-info img-button" disabled>
                                    <div class="img-wrap">
                                        <div class="img-spinner">
                                            <div class="spinner-border text-info" role="status">
                                                <span class="visually-hidden">{$_('Traitement…')}.</span>
                                            </div>
                                        </div>
                                        {#if att.previewImage}
                                            <img class="checkered" src={att.previewImage} alt={att.file.name} />
                                        {/if}
                                    </div>
                                    <div class="img-desc">{att.file.name}</div>
                                </button>
                            {:else}
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary img-button"
                                    class:active={imgId === att.id}
                                    onclick={() => {
                                        imgId = att.id
                                        imgUri = att.url
                                        imgAlt = att.filename
                                    }}
                                >
                                    <span class="img-wrap"><img class="checkered" src={selectSrc(att, previewName).url} alt={att.filename} /></span>
                                    <span class="img-desc">{att.filename}</span>
                                </button>
                            {/if}
                        </div>
                    {/each}
                </div>
            </div>
        </div>
    {/if}
    <div class="modal-footer text-bg-light">
        {#if Document}
            <div class="upload-zone">
                <Dropzone on:drop={handleFile} accept={[...$allowedMimetypes].join(',')} multiple={true}>
                    <div class="d-flex gap-2">
                        <div><Icon inline icon="mdi:image-size-select-actual" /></div>
                        <div class="col">
                            {$_('Cliquer ou glisser/déposer des images.')}
                        </div>
                    </div>
                </Dropzone>
            </div>
            <div class="col"></div>
        {/if}
        <button
            type="button"
            class="btn btn-outline-secondary"
            onclick={() => {
                close()
            }}>{$_('Fermer')}</button
        >
        <button
            type="button"
            class="btn btn-primary"
            disabled={!imgUri}
            onclick={() => {
                insertImage()
            }}>{$_('Ajouter l’image')}</button
        >
    </div>
</Modal>
